'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { BuyerLayoutProps } from 'app/shop-ui/types';
import { useScreenStore } from '@shared/ui';
import { getComponentMenu } from 'app/apis/component';
import i18n from '@shared/i18n';

const defaultNavigation: Menu<DetailMenuNodes[]> = {
    id: 'default-navigation',
    name: 'Default Navigation',
    next: [
        {
            id: 'default-navigation-1',
            name: i18n.gettext('頁面一'),
            type: 'none',
            value: 'default-navigation-1',
            next: [
                {
                    id: 'default-navigation-1-1',
                    name: i18n.gettext('子頁面一'),
                    type: 'none',
                    value: 'default-navigation-1-1',
                    next: [],
                },
            ],
        },
        {
            id: 'default-navigation-2',
            name: i18n.gettext('頁面二'),
            type: 'none',
            value: 'default-navigation-2',
            next: [
                {
                    id: 'default-navigation-2-1',
                    name: i18n.gettext('子頁面一'),
                    type: 'none',
                    value: 'default-navigation-2-1',
                    next: [],
                },
            ],
        },
        {
            id: 'default-navigation-3',
            name: i18n.gettext('頁面三'),
            type: 'none',
            value: 'default-navigation-3',
            next: [
                {
                    id: 'default-navigation-3-1',
                    name: i18n.gettext('子頁面一'),
                    type: 'none',
                    value: 'default-navigation-3-1',
                    next: [],
                },
            ],
        },
        {
            id: 'default-navigation-4',
            name: i18n.gettext('頁面四'),
            type: 'none',
            value: 'default-navigation-4',
            next: [
                {
                    id: 'default-navigation-4-1',
                    name: i18n.gettext('子頁面一'),
                    type: 'none',
                    value: 'default-navigation-4-1',
                    next: [],
                },
            ],
        },
    ],
};

const buyerSideDefaultNavigation: Menu<DetailMenuNodes[]> = {
    id: 'default-empty-navigation',
    name: i18n.gettext('預設商品分類頁'),
    next: [
        {
            id: 'default-navigation-to-browse-page',
            name: i18n.gettext('全部商品'),
            type: 'url',
            value: null,
            url: '/browse',
        },
    ],
};

interface UseNavigationProps extends BuyerLayoutProps {
    isBuyer?: boolean;
    id?: string;
    openInNewTab: boolean;
    isResponsiveType: boolean;
    menuRef: React.RefObject<HTMLDivElement>;
    isEditingMode?: boolean;
}

export function useNavigation(props: UseNavigationProps) {
    const { isBuyer, id, openInNewTab, menuRef, isResponsiveType } = props;

    const { status: screenStatus } = useScreenStore();

    const [isHeaderNav, setIsHeaderNav] = useState(false);

    const [isFooterNav, setIsFooterNav] = useState(false);

    const [isBodyNav, setIsBodyNav] = useState(false);

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const [navigation, setNavigation] = useState<Menu<DetailMenuNodes[]> | null>(
        isBuyer ? null : defaultNavigation
    );

    const [isMenuLoaded, setIsMenuLoaded] = useState(false);

    const prevIdRef = useRef('');

    const [navigationGroup, setNavigationGroup] = useState<DetailMenuNodes[][]>([]);

    const [currentNode, setCurrentNode] = useState<string[]>([]);

    const currentNodeLevel = useMemo(() => {
        return Math.max(currentNode.length - 1, 0);
    }, [currentNode]);

    function handleUpdateNavigationGroup(
        level: number,
        group: DetailMenuNodes[],
        nodeValue: string | null = null
    ) {
        if (group.length) {
            setNavigationGroup((prev) => {
                const newNavigationGroup = level === 0 ? [] : [...prev];

                newNavigationGroup[level] = group;

                return newNavigationGroup;
            });

            if (nodeValue) {
                setCurrentNode((prev) => {
                    const newCurrentNode = [...prev];

                    newCurrentNode[level] = nodeValue;

                    return newCurrentNode;
                });
            }
        }
    }

    function handleNavigationGroupOpen(
        isOpen: boolean,
        level: number,
        group: DetailMenuNodes[],
        nodeValue: string | null = null
    ) {
        const _isOpen = isOpen && currentNode[level] !== nodeValue;

        if (_isOpen) {
            handleUpdateNavigationGroup(level, group, nodeValue);

            if (!isResponsiveType && (screenStatus.isTablet || screenStatus.isMobile)) {
                setIsMobileMenuOpen(true);
            }
        } else {
            setNavigationGroup([]);
            setCurrentNode([]);
        }

        setIsMenuOpen(_isOpen);
    }

    function handleMobileMenuToggle() {
        setIsMobileMenuOpen((prev) => !prev);
    }

    function handleMenuStatusReset() {
        setIsMenuOpen(false);
        setIsMobileMenuOpen(false);
        setNavigationGroup([]);
        setCurrentNode([]);
    }

    function handleMenuBack() {
        setNavigationGroup((prev) => {
            const newNavigationGroup = [...prev];

            newNavigationGroup.pop();

            return newNavigationGroup;
        });

        setCurrentNode((prev) => {
            const newCurrentNode = [...prev];

            newCurrentNode.pop();

            return newCurrentNode;
        });
    }

    useEffect(() => {
        if (!id && !isBuyer) {
            prevIdRef.current = '';

            setNavigation(defaultNavigation);

            setIsMenuLoaded(true);

            return;
        }

        if (!id && isBuyer && isHeaderNav) {
            setNavigation(buyerSideDefaultNavigation);

            setIsMenuLoaded(true);

            return;
        }

        if (id && prevIdRef.current !== id) {
            prevIdRef.current = id;

            getComponentMenu(id, isBuyer)
                .then((res) => {
                    if (res?.data) {
                        setNavigation(res.data);
                    }
                })
                .finally(() => {
                    setIsMenuLoaded(true);
                });

            return;
        }

        setIsMenuLoaded(true);
    }, [id, isBuyer, isHeaderNav]);

    useEffect(() => {
        const element = menuRef as React.RefObject<HTMLDivElement>;

        // 拿 header 和 footer 的背景顏色
        if (element?.current && typeof window !== 'undefined' && window) {
            const headerContainer = element.current.closest(
                '[data-shop-component-header]'
            ) as HTMLElement;

            const footerContainer = element.current.closest(
                '[data-shop-component-footer]'
            ) as HTMLElement;

            if (headerContainer) {
                setIsHeaderNav(true);

                const container = headerContainer.querySelector(
                    '[data-shop-component-container-id]'
                ) as HTMLElement;

                if (container) {
                    let headerBackgroundColor = window
                        .getComputedStyle(container)
                        .getPropertyValue('background-color');

                    if (headerBackgroundColor === 'rgba(0, 0, 0, 0)') {
                        headerBackgroundColor = 'rgba(255, 255, 255, 1)';
                    }

                    if (headerBackgroundColor) {
                        headerContainer.style.setProperty(
                            '--shop-ui-custom-header-menu-background-color',
                            headerBackgroundColor
                        );
                    }
                }
            } else if (footerContainer) {
                setIsFooterNav(true);

                const container = footerContainer.querySelector(
                    '[data-shop-component-container-id]'
                ) as HTMLElement;

                if (container) {
                    let footerBackgroundColor = window
                        .getComputedStyle(
                            footerContainer.querySelector(
                                '[data-shop-component-container-id]'
                            ) as HTMLElement
                        )
                        .getPropertyValue('background-color');

                    if (footerBackgroundColor === 'rgba(0, 0, 0, 0)') {
                        footerBackgroundColor = 'rgba(255, 255, 255, 1)';
                    }

                    if (footerBackgroundColor) {
                        footerContainer.style.setProperty(
                            '--shop-ui-custom-footer-menu-background-color',
                            footerBackgroundColor
                        );
                    }
                }
            } else {
                setIsBodyNav(true);
            }

            // menu 如果為響應式，則特規處理 Layout 的樣式
            if (isResponsiveType) {
                const cell = element.current.closest(
                    '[data-shop-component-grid-cell]'
                ) as HTMLElement;

                if (headerContainer) {
                    headerContainer.classList.add('header-container--responsive');

                    headerContainer.style.setProperty(
                        '--shop-ui-custom-header-menu-width',
                        '32px'
                    );

                    if (cell && cell.parentElement) {
                        headerContainer.style.setProperty(
                            '--shop-ui-custom-header-menu-color',
                            element.current.style.getPropertyValue('color')
                        );

                        cell.parentElement.classList.add('header-search--responsive');

                        headerContainer.style.setProperty(
                            '--shop-ui-custom-header-menu-position',
                            cell.offsetLeft + 'px'
                        );
                    }
                }

                if (footerContainer) {
                    footerContainer.classList.add('footer-container--responsive');

                    footerContainer.style.setProperty(
                        '--shop-ui-custom-footer-menu-width',
                        '32px'
                    );

                    if (cell) {
                        cell.classList.add('footer-menu--responsive');
                    }
                }
            }
        }
    }, [menuRef, isResponsiveType]);

    useEffect(() => {
        function catchClickEventOuterContainer(e: MouseEvent) {
            const element = menuRef;

            if (isMenuOpen && !isMobileMenuOpen && element.current) {
                const target = e.target as HTMLElement;

                if (!element.current.contains(target)) {
                    handleMenuStatusReset();
                }
            }
        }

        function getMenuGroupPositionTop() {
            const element = menuRef;

            if (element.current) {
                const footerContainer = element.current.closest(
                    '[data-shop-component-footer]'
                ) as HTMLElement;

                if (footerContainer && element.current) {
                    const { top } = footerContainer.getBoundingClientRect();

                    element.current.style.setProperty(
                        '--shop-ui-custom-footer-position-top',
                        top + 'px'
                    );
                }
            }
        }

        window.addEventListener('scroll', getMenuGroupPositionTop);

        window.addEventListener('click', catchClickEventOuterContainer);

        return () => {
            window.removeEventListener('click', catchClickEventOuterContainer);

            window.removeEventListener('scroll', getMenuGroupPositionTop);
        };
    }, [isMenuOpen, menuRef, isMobileMenuOpen]);

    const state = {
        isHeaderNav,
        isFooterNav,
        isBodyNav,
        navigation,
        navigationGroup,
        isMenuOpen,
        isMenuLoaded,
        openInNewTab,
        isMobileMenuOpen,
        isResponsiveType,
        currentNode,
        currentNodeLevel,
    };

    const actions = {
        handleMenuStatusReset,
        handleNavigationGroupOpen,
        handleMobileMenuToggle,
        handleUpdateNavigationGroup,
        handleMenuBack,
    };

    return {
        state,
        actions,
    };
}

export type UseNavigationReturn = ReturnType<typeof useNavigation>;
